import { AsyncPipe } from '@angular/common';
import { Component, HostListener, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { DialogService, DynamicDialogConfig, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SkeletonModule } from 'primeng/skeleton';
import { firstValueFrom, lastValueFrom } from 'rxjs';

import { WorkPackagePickerComponent } from '../../components-form/work-package-picker/work-package-picker.component';
import { CreateDialogLayoutComponent } from '../../layouts/create-dialog-layout/create-dialog-layout.component';
import { WorkPackagesService } from '../../services/work-package.service';
import { cast } from '../../utils';
import { SipalCorrettivaRecord } from '../sipal.model';
import { SipalService } from '../sipal.service';
import {
  AttachmentScope,
  BaseFeatureDto,
  BaseGroupDto,
  BaseWorkPackageDto,
  BaseWorkPackageUserDto,
  ProjectDto,
  WorkPackagePriorityDto,
  WorkPackageTypeDto,
} from '@api/backend-connector/models';
import { AttachmentsManagerComponent } from 'src/app/attachments-manager/attachments-manager.component';
import { AttachmentsUploadHandler } from 'src/app/attachments-manager/attatchments-upload.handler';
import { WpAssigneesComponent } from 'src/app/components/wp-assignees/wp-assignees.component';
import { ConstantPickerComponent } from 'src/app/components-form/constant-picker/constant-picker.component';
import { GroupPickerComponent } from 'src/app/components-form/group-picker/group-picker.component';
import { SmartInputRichTextComponent } from 'src/app/components-form/smart-input-rich-text/smart-input-rich-text.component';
import { SmartInputStringComponent } from 'src/app/components-form/smart-input-string/smart-input-string.component';
import { WPPriorityPickerComponent } from 'src/app/components-form/wp-priority-picker/wp-priority-picker.component';
import { WPStatusPickerComponent } from 'src/app/components-form/wp-status-picker/wp-status-picker.component';
import { WPTypePickerComponent } from 'src/app/components-form/wp-type-picker/wp-type-picker.component';
import { AttachmentsService } from 'src/app/services/attachments.service';
import { CurrentProjectService } from 'src/app/services/current-project.service';
import { ToastService } from 'src/app/services/toast.service';

@Component({
  selector: 'app-sipal-work-package-create-dialog',
  templateUrl: './sipal-work-package-create-dialog.component.html',
  styleUrls: ['./sipal-work-package-create-dialog.component.scss'],
  standalone: true,
  imports: [
    AsyncPipe,
    AttachmentsManagerComponent,
    CheckboxModule,
    CreateDialogLayoutComponent,
    ConstantPickerComponent,
    DropdownModule,
    DynamicDialogModule,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    ReactiveFormsModule,
    SkeletonModule,
    SmartInputRichTextComponent,
    SmartInputStringComponent,
    TranslateModule,
    WpAssigneesComponent,
    GroupPickerComponent,
    WorkPackagePickerComponent,
    WPPriorityPickerComponent,
    WPStatusPickerComponent,
    WPTypePickerComponent,
  ],
})
export class SipalWorkPackageCreateDialogComponent {
  @HostListener('document:keydown.enter', ['$event'])
  onEnterPress(event: KeyboardEvent): void {
    void this.submit(event);
  }

  private readonly sipalService = inject(SipalService);
  private readonly fb = inject(FormBuilder);
  readonly dialog = inject(DialogService);
  readonly dialogRef = inject(DynamicDialogRef);
  readonly data: {
    project: ProjectDto;
    priorities: WorkPackagePriorityDto[];
    parent: BaseWorkPackageDto | undefined;
  } = inject(DynamicDialogConfig).data;
  readonly attachmentsService = inject(AttachmentsService);
  private readonly workPackagesService = inject(WorkPackagesService);
  private readonly currentProjectService = inject(CurrentProjectService);
  private readonly toast = inject(ToastService);

  readonly pagePermissions$ = this.currentProjectService.workPackagesPageConfAndPermissions(this.data.project.id);

  readonly cast = cast<BaseWorkPackageDto>;

  project: ProjectDto;
  initialPriority;
  initialType;
  initialStatus;

  readonly attachmentsUploadHandler = new AttachmentsUploadHandler(this.attachmentsService, this.toast, {
    showSuccessToast: false,
    existent: [],
    assetId: undefined,
    draft: true,
    scope: AttachmentScope.WorkPackage,
    mode: 'form',
    canEdit: true,
  });

  get isCorrettiva() {
    return this.data.project.data.projectType === 'correttive';
  }

  get isInitialized() {
    return this.isCorrettiva ? !!this.correttiveRecords : true;
  }

  get formDisabled() {
    return this.isCorrettiva ? this.correttivaForm.disabled : this.form.disabled;
  }

  correttivaForm = this.fb.group({
    impianto: [{ value: null as unknown as string, disabled: false }, [Validators.required]],
    ubicazione: [{ value: null as unknown as string, disabled: true }, [Validators.required]],
    assetL1: [{ value: null as unknown as string, disabled: true }, [Validators.required]],
    assetL2: [{ value: null as unknown as string, disabled: true }, [Validators.required]],
    guasto: [{ value: null as unknown as string, disabled: true }, [Validators.required]],
    guastoCustom: [{ value: null as unknown as string, disabled: true }],
    name: this.fb.nonNullable.control<string>('', Validators.required),
    projectId: this.fb.nonNullable.control<string>(this.data.project.id, Validators.required),
    description: this.fb.nonNullable.control<string | undefined>(undefined),
    parent: this.fb.nonNullable.control<BaseWorkPackageDto | undefined>(this.data.parent),
    workPackagePriority: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    workPackageStatus: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    workPackageType: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    assignees: this.fb.nonNullable.array<BaseWorkPackageUserDto>([]),
    group: this.fb.nonNullable.control<BaseGroupDto>(null as unknown as BaseGroupDto, [Validators.required]),
  });

  // Records
  correttiveRecords!: SipalCorrettivaRecord[];
  // SK
  skCorrettiva!: unknown;
  // Guasti Map
  guastiMap!: Map<string, string[]>;

  // Selezioni dell'utente
  selectedImpianto: string | null = null;
  selectedUbicazione: string | null = null;
  selectedAssetL1: string | null = null;
  selectedAssetL2: string | null = null;

  selectedRecord?: SipalCorrettivaRecord;

  // Opzioni per i dropdown
  impiantoOptions: string[] = [];
  ubicazioneOptions: string[] = [];
  assetL1Options: string[] = [];
  assetL2Options: string[] = [];
  guastoOptions: string[] = [];

  constructor() {
    this.project = this.data.project;

    const workPackageType = this.project.workPackageTypes
      .sort((t1, t2) => (t1.position > t2.position ? 1 : -1))
      .at(0) as WorkPackageTypeDto;
    this.initialType = workPackageType;
    this.initialStatus = workPackageType.defaultWorkPackageStatus;
    this.initialPriority = this.data.priorities.find((wpp) => wpp.isDefault) as WorkPackagePriorityDto;

    this.form.controls.workPackageType.patchValue(this.initialType);
    this.form.controls.workPackageStatus.patchValue(this.initialStatus);
    this.form.controls.workPackagePriority.patchValue(this.initialPriority);

    this.correttivaForm.controls.workPackageType.patchValue(this.initialType);
    this.correttivaForm.controls.workPackageStatus.patchValue(this.initialStatus);
    this.correttivaForm.controls.workPackagePriority.patchValue(this.initialPriority);

    void this.init();

    // Resetta valore, disabilita controllo e svuota le opzioni
    const resetLevels = (controls: string[]): void => {
      controls.forEach((key) => {
        // patchValue con emitEvent: false evita loop infiniti o trigger indesiderati
        this.correttivaForm.get(key)?.patchValue(null, { emitEvent: false });
        this.correttivaForm.get(key)?.disable({ emitEvent: false });

        // Svuota l'array delle opzioni corrispondente
        if (key === 'ubicazione') this.ubicazioneOptions = [];
        if (key === 'assetL1') this.assetL1Options = [];
        if (key === 'assetL2') this.assetL2Options = [];
        if (key === 'guasto') this.guastoOptions = [];
      });
    };

    this.correttivaForm.controls.impianto.valueChanges.pipe(takeUntilDestroyed()).subscribe((impianto) => {
      resetLevels(['ubicazione', 'assetL1', 'assetL2', 'guasto', 'guastoCustom']);

      if (impianto) {
        const filtered = this.correttiveRecords.filter((d) => d.IMPIANTO === impianto);
        const opts = [...new Set(filtered.map((d) => d.UBICAZIONE))];
        this.ubicazioneOptions = opts;

        this.correttivaForm.controls.ubicazione.enable();
      }
    });

    // --- LIVELLO 2: Cambio UBICAZIONE ---
    this.correttivaForm.controls.ubicazione.valueChanges.pipe(takeUntilDestroyed()).subscribe((ubicazione) => {
      resetLevels(['assetL1', 'assetL2', 'guasto', 'guastoCustom']);

      const impianto = this.correttivaForm.controls.impianto.value;

      if (ubicazione && impianto) {
        const filtered = this.correttiveRecords.filter((d) => d.IMPIANTO === impianto && d.UBICAZIONE === ubicazione);
        const opts = [...new Set(filtered.map((d) => d['DESCRIZIONE ASSET 1° LIVELLO']))];
        this.assetL1Options = opts;

        this.correttivaForm.controls.assetL1.enable();
      }
    });

    // --- LIVELLO 3: Cambio ASSET L1 ---
    this.correttivaForm.controls.assetL1.valueChanges.pipe(takeUntilDestroyed()).subscribe((asset1) => {
      resetLevels(['assetL2']);
      resetLevels(['guasto', 'guastoCustom']);

      const impianto = this.correttivaForm.controls.impianto.value;
      const ubicazione = this.correttivaForm.controls.ubicazione.value;

      if (asset1 && ubicazione && impianto) {
        const filtered = this.correttiveRecords.filter(
          (d) => d.IMPIANTO === impianto && d.UBICAZIONE === ubicazione && d['DESCRIZIONE ASSET 1° LIVELLO'] === asset1,
        );
        const opts = [...new Set(filtered.map((d) => d['DESCRIZIONE ASSET 2° LIVELLO']))];
        this.assetL2Options = opts;

        this.correttivaForm.controls.assetL2.enable();
      }
    });

    // --- LIVELLO 4: Cambio ASSET L2 (Selezione finale) ---
    this.correttivaForm.controls.assetL2.valueChanges.pipe(takeUntilDestroyed()).subscribe((asset2) => {
      resetLevels(['guasto', 'guastoCustom']);

      const impianto = this.correttivaForm.controls.impianto.value;
      const ubicazione = this.correttivaForm.controls.ubicazione.value;
      const asset1 = this.correttivaForm.controls.assetL1.value;

      if (asset2 && asset1 && ubicazione && impianto) {
        this.selectedRecord = this.correttiveRecords.find(
          (record) =>
            record.IMPIANTO === impianto &&
            record.UBICAZIONE === ubicazione &&
            record['DESCRIZIONE ASSET 1° LIVELLO'] === asset1 &&
            record['DESCRIZIONE ASSET 2° LIVELLO'] === asset2,
        );
        if (!this.selectedRecord) {
          throw new Error('Unexpected');
        }
        this.correttivaForm.controls.name.patchValue(this.selectedRecord.taskName);

        this.guastoOptions = this.guastiMap.get(asset2) ?? [];
        this.correttivaForm.controls.guasto.enable();
        this.correttivaForm.controls.guastoCustom.enable();
      }
    });

    // --- LIVELLO 5: Cambio GUASTO (reset custom) ---
    this.correttivaForm.controls.guasto.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.correttivaForm.controls.guastoCustom.patchValue(null, { emitEvent: false });
    });
  }

  async init() {
    this.correttiveRecords = await firstValueFrom(this.sipalService.correttiveRecords$);
    this.skCorrettiva = await firstValueFrom(this.sipalService.correttivaSK$);
    this.guastiMap = await firstValueFrom(this.sipalService.guastiMap$);
    this.impiantoOptions = [...new Set(this.correttiveRecords.map((item) => item.IMPIANTO))];
  }

  readonly form = this.fb.nonNullable.group({
    name: this.fb.nonNullable.control<string>('', Validators.required),
    projectId: this.fb.nonNullable.control<string>(this.data.project.id, Validators.required),
    description: this.fb.nonNullable.control<string | undefined>(undefined),
    parent: this.fb.nonNullable.control<BaseWorkPackageDto | undefined>(this.data.parent),
    workPackagePriority: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    workPackageStatus: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    workPackageType: this.fb.nonNullable.control<BaseFeatureDto>(
      null as unknown as BaseFeatureDto,
      Validators.required,
    ),
    assignees: this.fb.nonNullable.array<BaseWorkPackageUserDto>([]),
  });

  static async open(
    dialog: DialogService,
    project: ProjectDto,
    priorities: WorkPackagePriorityDto[],
    parent?: BaseWorkPackageDto,
  ): Promise<BaseWorkPackageDto | undefined> {
    return (await lastValueFrom(
      dialog.open(SipalWorkPackageCreateDialogComponent, {
        data: { project, priorities, parent },
        position: 'center',
        showHeader: false,
        styleClass: 'custom-upsert-dialog',
      }).onClose,
    )) as BaseWorkPackageDto | undefined;
  }

  onDialogAction(action: 'close' | 'save') {
    switch (action) {
      case 'close':
        return this.dialogRef.close();
      case 'save':
        return this.submit();
    }
  }

  async submit(event?: KeyboardEvent) {
    return this.isCorrettiva ? this.submitCorrettiva(event) : this.submitProgrammata(event);
  }

  async submitProgrammata(event?: KeyboardEvent) {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    let created: BaseWorkPackageDto | undefined;

    try {
      event?.stopPropagation();
      this.form.disable({ emitEvent: false });
      const value = this.form.getRawValue();

      const attachments = await firstValueFrom(this.attachmentsUploadHandler.currentAttachments$);
      created = await firstValueFrom(
        this.workPackagesService.createWorkPackage({
          name: value.name,
          description: value.description,
          parentId: value.parent?.id,
          projectId: value.projectId,
          workPackagePriorityId: value.workPackagePriority.id,
          workPackageStatusId: value.workPackageStatus.id,
          workPackageTypeId: value.workPackageType.id,
          startDate: value.parent ? value.parent.startDate : undefined,
          endDate: value.parent ? value.parent.endDate : undefined,
          customFields: {},
          dynamicFormsSchema: {},
          dynamicFormsValue: {},
          attachments,
          workPackageCategories: [],
          assignees: value.assignees.map((a) => a.id),
          groups: [],
        }),
      );

      this.workPackagesService.refresh$.next();
      this.attachmentsUploadHandler.setAssetId(created.id);
      this.workPackagesService.showCreateSuccessToast(this.toast);
      this.dialogRef.close(created);
    } catch {
      this.form.enable();
    }
  }

  async submitCorrettiva(event?: KeyboardEvent) {
    this.correttivaForm.markAllAsTouched();

    if (this.correttivaForm.invalid) {
      return;
    }

    let created: BaseWorkPackageDto | undefined;

    try {
      event?.stopPropagation();
      const value = this.correttivaForm.getRawValue();
      this.form.disable({ emitEvent: false });

      const attachments = await firstValueFrom(this.attachmentsUploadHandler.currentAttachments$);
      created = await firstValueFrom(
        this.workPackagesService.createWorkPackage({
          name: `${value.name} ${value.guasto}`,
          description: value.description,
          parentId: undefined,
          projectId: this.data.project.id,
          workPackagePriorityId: value.workPackagePriority.id,
          workPackageStatusId: value.workPackageStatus.id,
          workPackageTypeId: value.workPackageType.id,
          startDate: new Date().toISOString(),
          endDate: value.parent ? value.parent.endDate : undefined,
          customFields: {
            asset: this.selectedRecord?.assetGuid,
            guasto: value.guastoCustom ?? value.guasto,
          },
          dynamicFormsSchema: this.skCorrettiva,
          dynamicFormsValue: {},
          attachments,
          workPackageCategories: [],
          assignees: value.assignees.map((a) => a.id),
          groups: [value.group.id],
        }),
      );

      this.workPackagesService.refresh$.next();
      this.attachmentsUploadHandler.setAssetId(created.id);
      this.workPackagesService.showCreateSuccessToast(this.toast);
      this.dialogRef.close(created);
    } catch {
      this.correttivaForm.enable();
    }
  }
}
